"use client";

import { MessageCircleQuestionMark as MessageCircleQuestion } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const faqs = [{
  question: "How may I monitor the journey of my boutique acquisition?",
  answer: "As soon as your order ships, we'll email you a direct tracking link. You can easily follow your package every step of the way right to your door."
}, {
  question: "What are the protocols for returns and exchanges?",
  answer: "We offer hassle-free returns within 30 days for all unworn items in their original condition. Just reach out to our team and we'll send you a prepaid return shipping label."
}, {
  question: "Does the Maison offer international white-glove delivery?",
  answer: "Drip District proudly ships worldwide to over 60 countries using top-tier express couriers. All customs duties and shipping fees are calculated clearly at checkout."
}, {
  question: "How should I preserve the integrity of my luxury garments?",
  answer: "Each piece comes with easy-to-follow care instructions tailored to its specific fabric. We recommend eco-friendly dry cleaning to keep your luxury items looking pristine."
}, {
  question: "Is it possible to amend a commission after placement?",
  answer: "Because we pack and ship orders quickly, we have a short 60-minute window for order changes. Drop us a message right away if you need to update anything."
}, {
  question: "What defines the Maison's commitment to sustainable artistry?",
  answer: "We care deeply about the planet. We use GOTS-certified organic fabrics and partner exclusively with ethical makers who guarantee fair wages and safe conditions."
}, {
  question: "How do I ensure an impeccable fit for my silhouette?",
  answer: "Check out our detailed size guide on every product page for exact measurements. If you're ever unsure, our styling team is always happy to help you pick the right size."
}, {
  question: "When will my account be credited for a return?",
  answer: "Once our returns team inspects and processes your items, your refund will be issued back to your original payment method within 5 to 7 business days."
}, {
  question: "Are the seasonal collections produced in limited quantities?",
  answer: "Yes! To keep our styles exclusive and reduce waste, our drops are made in small batches. We recommend grabbing your favorite pieces before they sell out."
}, {
  question: "Does the Maison provide bespoke tailoring services?",
  answer: "We don't offer direct tailoring, but our garments are designed with extra seam allowances so your local tailor can easily customize the fit."
}, {
  question: "How is my private data and transaction security managed?",
  answer: "Your security is our top priority. We use bank-grade SSL encryption and secure, PCI-compliant payment gateways to protect your personal data every time you shop with Drip District."
}, {
  question: "How may I gain entry to the Drip District Loyalty Circle?",
  answer: "Joining is automatic! As soon as you make your first purchase, you're unlocked into our loyalty program, giving you early access to new drops and secret sales."
}];
export default function Inquiries() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="1315" id="common-inquiries" className="bg-background text-foreground py-24 md:py-40 px-6 md:px-12">
      <div ref={ref} className={`mx-auto max-w-[1440px] transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-[120px]">
              <div className="flex items-center gap-3 mb-8">
                <span className="font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground">
                  § LSH-FA · 02 / COMMON INQUIRIES
                </span>
                <span className="inline-block w-2 h-2" style={{
                backgroundColor: "var(--accent)"
              }} />
              </div>

              <h2 className="font-default font-extrabold text-5xl md:text-7xl leading-[0.85] tracking-tighter text-foreground mb-10">
                <span>Guidance for </span>
                <span className="relative inline-block">
                  <svg className="absolute -inset-x-3 -inset-y-2 w-[calc(100%+1.5rem)] h-[calc(100%+1rem)] pointer-events-none" viewBox="0 0 120 60" preserveAspectRatio="none" fill="none" aria-hidden="true">
                    <path d="M 8 30 Q 4 14, 30 8 Q 60 2, 95 6 Q 116 12, 114 32 Q 110 52, 78 54 Q 40 58, 18 52 Q 2 46, 8 30 Z" stroke="var(--primary)" strokeWidth="3" strokeLinecap="round" fill="none" style={{
                    strokeDasharray: 400,
                    strokeDashoffset: isVisible ? 0 : 400,
                    transition: "stroke-dashoffset 600ms cubic-bezier(0.22, 1, 0.36, 1) 300ms",
                    transform: "rotate(-2deg)",
                    transformOrigin: "center"
                  }} />
                  </svg>
                  <span className="relative">discerning</span>
                </span>
                <span> clientele.</span>
              </h2>

              <p className="font-default text-lg text-muted-foreground leading-relaxed mb-10">
                Discover the nuances of the Drip District experience. Our concierge 
                is dedicated to providing transparent, sophisticated support tailored 
                to your unique sartorial journey.
              </p>

              <Link to="/CustomerCarePage" className="group inline-flex items-center gap-2 font-default font-bold text-foreground hover:text-foreground">
                <span className="relative">
                  Consult with our concierge
                  <span className="absolute left-0 -bottom-0.5 h-[2px] w-0 group-hover:w-full transition-all duration-300 ease-out" style={{
                  backgroundColor: "var(--primary)"
                }} />
                </span>
                <span className="transition-transform duration-300 group-hover:translate-x-1" style={{
                color: "var(--primary)"
              }}>
                  →
                </span>
              </Link>

              <div className="mt-12 hidden lg:block">
                <svg viewBox="0 0 180 220" className="w-44 h-auto" aria-hidden="true">
                  <line x1="90" y1="30" x2="90" y2="55" stroke="var(--foreground)" strokeWidth="3" strokeLinecap="square" />
                  <circle cx="90" cy="22" r="8" fill="var(--accent)" stroke="var(--foreground)" strokeWidth="3" />
                  <rect x="40" y="55" width="100" height="90" fill="var(--secondary)" stroke="var(--foreground)" strokeWidth="3" />
                  <circle cx="68" cy="92" r="5" fill="var(--foreground)" />
                  <circle cx="112" cy="92" r="5" fill="var(--foreground)" />
                  <rect x="55" y="145" width="70" height="55" fill="var(--secondary)" stroke="var(--foreground)" strokeWidth="3" />
                </svg>
              </div>
            </div>
          </div>

          <div className="lg:col-span-8">
            <Accordion type="single" collapsible className="flex flex-col gap-0">
              {faqs.map((faq, i) => <AccordionItem key={i} value={`item-${i}`} className="border-t border-border last:border-b" data-index={i}>
                  <AccordionTrigger className="group hover:no-underline py-8 px-0 [&>svg]:hidden">
                    <div className="flex items-center gap-6 flex-1 text-left">
                      <span className="font-mono text-xs tracking-[0.1em] uppercase text-muted-foreground shrink-0 w-12">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="font-default font-bold text-2xl text-foreground leading-snug flex-1">
                        {faq.question}
                      </span>
                      <span className="shrink-0 inline-flex items-center justify-center w-10 h-10 border border-foreground transition-all duration-300 group-data-[state=open]:rotate-45">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                          <path d="M8 2 V14 M2 8 H14" stroke="currentColor" strokeWidth="2" strokeLinecap="square" />
                        </svg>
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-8">
                    <div className="pl-[4.5rem]">
                      <p className="font-default text-lg text-muted-foreground leading-relaxed max-w-2xl">
                        {faq.answer}
                      </p>
                      <Link to="/ShopPage" className="group/link mt-6 inline-flex items-center gap-2 font-default font-bold text-sm text-foreground hover:text-foreground">
                        <MessageCircleQuestion className="w-4 h-4" style={{
                      color: "var(--primary)"
                    }} />
                        <span className="relative">
                          Explore the collection
                          <span className="absolute left-0 -bottom-0.5 h-[1px] w-0 group-hover/link:w-full transition-all duration-300 ease-out" style={{
                        backgroundColor: "var(--primary)"
                      }} />
                        </span>
                      </Link>
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>

            <div className="mt-20 p-10 bg-secondary border border-border relative overflow-hidden">
              <div className="relative z-10">
                <h3 className="font-default font-bold text-3xl text-foreground mb-4">
                  Bespoke Assistance
                </h3>
                <p className="font-default text-lg text-muted-foreground mb-8 max-w-xl">
                  Should you require further guidance, our private concierge remains at your disposal for personalized advice on acquisitions, sizing, and atelier services.
                </p>
                <Link to="/CustomerCarePage" className="inline-block bg-primary text-primary-foreground font-default font-bold px-8 py-4 hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[6px_6px_0px_0px_var(--accent)] transition-all duration-200">
                  Engage Our Private Concierge
                </Link>
              </div>
              <div className="absolute top-0 right-0 -mt-4 -mr-4 opacity-5 pointer-events-none">
                <MessageCircleQuestion className="size-48" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}